import { useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Printer, Send, Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable Print + Welcome Email components for staff onboarding.
 * Print uses window.print() with a print-only stylesheet to render just the ID card.
 */

const cardHtml = (staff, hospital) => {
  const u = staff.user || {};
  const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  const initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`;
  const hospitalName = hospital?.name || 'Klick Niramaya HMS';
  const hospitalAddr = hospital?.address || hospital?.branding?.tagline || '';
  return `
  <div class="card">
    <div class="head">
      <div>
        <div class="hosp">${hospitalName}</div>
        ${hospitalAddr ? `<div class="tag">${hospitalAddr}</div>` : ''}
      </div>
      <div class="hosp" style="font-size:5pt;">EMPLOYEE ID</div>
    </div>
    <div class="body">
      <div class="ph">${initials}</div>
      <div class="info">
        <p class="name">${fullName}</p>
        <p class="role">${staff.designation || ''} · ${staff.department || ''}</p>
        <div class="row"><strong>ID:</strong> ${staff.employee_code || '—'}</div>
        <div class="row"><strong>Role:</strong> ${u.role_name || '—'}</div>
        ${u.phone ? `<div class="row"><strong>Ph:</strong> ${u.phone}</div>` : ''}
        ${staff.blood_group ? `<div class="row"><strong>Blood:</strong> ${staff.blood_group}</div>` : ''}
      </div>
    </div>
    <div class="foot">
      <span>Joined: ${staff.joining_date || ''}</span>
      <span>If found, return</span>
    </div>
  </div>`;
};

const cardStyles = `
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font-family: 'Helvetica Neue', Arial, sans-serif; }
  .card { width: 86mm; height: 54mm; padding: 4mm; display: flex; flex-direction: column; background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%); color: white; position: relative; border-radius: 2mm; }
  .card::before { content: ''; position: absolute; inset: 1.5mm; border: 0.4mm solid rgba(255,255,255,0.3); border-radius: 1.5mm; pointer-events: none; }
  .head { display: flex; justify-content: space-between; align-items: center; }
  .hosp { font-size: 7pt; font-weight: 700; letter-spacing: 0.5px; }
  .tag { font-size: 5pt; opacity: 0.85; }
  .body { display: flex; gap: 3mm; flex: 1; align-items: center; margin-top: 2mm; }
  .ph { width: 18mm; height: 22mm; background: white; border-radius: 1mm; color: #0d9488; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16pt; flex-shrink: 0; }
  .info { flex: 1; min-width: 0; }
  .name { font-size: 11pt; font-weight: 700; margin: 0 0 1mm 0; }
  .role { font-size: 7pt; opacity: 0.95; margin: 0 0 2mm 0; }
  .row { font-size: 6pt; line-height: 1.5; opacity: 0.95; }
  .row strong { font-weight: 600; }
  .foot { display: flex; justify-content: space-between; font-size: 5pt; opacity: 0.8; margin-top: 1mm; }
`;

export const printBulkIDCards = (staffList, hospital) => {
  if (!staffList || !staffList.length) return;
  const win = window.open('', '_blank', 'width=900,height=1200');
  if (!win) { toast.error('Pop-ups blocked. Please allow pop-ups to print.'); return; }
  const cards = staffList.map(s => cardHtml(s, hospital)).join('');
  const html = `<!DOCTYPE html><html><head><title>ID Cards (${staffList.length})</title>
<style>
  @page { size: A4 portrait; margin: 10mm; }
  ${cardStyles}
  body { background: white; }
  .sheet { display: grid; grid-template-columns: repeat(2, 86mm); gap: 4mm 4mm; justify-content: center; }
  @media print { .sheet { page-break-inside: auto; } .card { page-break-inside: avoid; } }
</style></head><body>
<div class="sheet">${cards}</div>
<script>window.onload=function(){window.print();setTimeout(function(){window.close()},800);}</script>
</body></html>`;
  win.document.write(html);
  win.document.close();
};

export const StaffIDCardModal = ({ open, onClose, staff, hospital }) => {
  if (!staff) return null;
  const u = staff.user || {};
  const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  const initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`;
  const hospitalName = hospital?.name || 'Klick Niramaya HMS';
  const hospitalAddr = hospital?.address || hospital?.branding?.tagline || '';

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=600,height=400');
    if (!win) { toast.error('Pop-ups blocked. Please allow pop-ups to print.'); return; }
    const html = `<!DOCTYPE html><html><head><title>ID Card - ${fullName}</title>
<style>@page { size: 86mm 54mm; margin: 0; } ${cardStyles}</style></head><body>
${cardHtml(staff, hospital)}
<script>window.onload = function(){ window.print(); setTimeout(function(){window.close()}, 500); }</script>
</body></html>`;
    win.document.write(html);
    win.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Print Employee ID Card</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Preview */}
          <div className="rounded-xl overflow-hidden shadow-md" style={{ aspectRatio: '86 / 54' }}>
            <div className="h-full p-4 flex flex-col text-white relative" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              <div className="absolute inset-2 border border-white/30 rounded-lg pointer-events-none" />
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-bold tracking-wide">{hospitalName}</div>
                  {hospitalAddr && <div className="text-[10px] opacity-85">{hospitalAddr}</div>}
                </div>
                <div className="text-[10px] font-bold">EMPLOYEE ID</div>
              </div>
              <div className="flex gap-3 mt-2 flex-1 items-center">
                <div className="w-16 h-20 bg-white text-teal-700 rounded flex items-center justify-center font-extrabold text-2xl flex-shrink-0">{initials}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold mb-0.5 truncate">{fullName}</p>
                  <p className="text-[11px] opacity-95 mb-1.5 truncate">{staff.designation} · {staff.department}</p>
                  <div className="text-[10px] leading-snug opacity-95">
                    <div><strong className="font-semibold">ID:</strong> {staff.employee_code}</div>
                    <div><strong className="font-semibold">Role:</strong> {u.role_name}</div>
                    {u.phone && <div><strong className="font-semibold">Ph:</strong> {u.phone}</div>}
                    {staff.blood_group && <div><strong className="font-semibold">Blood:</strong> {staff.blood_group}</div>}
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-[9px] opacity-80">
                <span>Joined: {staff.joining_date}</span>
                <span>If found, please return</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={handlePrint} className="bg-teal-600 hover:bg-teal-700" data-testid="print-id-card-btn">
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const WelcomeEmailModal = ({ open, onClose, staff, hospital }) => {
  const [tempPassword, setTempPassword] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);

  if (!staff) return null;
  const u = staff.user || {};
  const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  const hospitalName = hospital?.name || 'Klick Niramaya HMS';
  const portalUrl = window.location.origin;
  const loginId = u.user_id || staff.employee_code || '(Login ID will appear here)';
  const tempPwLine = tempPassword
    ? tempPassword
    : '(click "Generate Temp Password" below to mint one)';

  const subject = `Welcome to ${hospitalName} — Your Login Credentials`;
  const body = `Dear ${fullName},

A warm welcome to ${hospitalName}!

We are delighted to have you join our team as ${staff.designation} in the ${staff.department} department. Your joining date is ${staff.joining_date}.

Your Klick Niramaya HMS account has been created. Use these credentials to log in:

  Portal:               ${portalUrl}
  Login ID (User ID):   ${loginId}
  Temporary Password:   ${tempPwLine}
  Registered Email:     ${u.email}
  Employee Code:        ${staff.employee_code}

How to sign in:
  1. Open the portal URL above.
  2. Enter your Login ID (the Employee ID shown above — e.g., PMHS0000001).
  3. Enter your Temporary Password.
  4. You will be prompted to set a new password on first login.
     - Minimum 12 characters
     - At least one uppercase, lowercase, digit, and special character (!@#$%^&*)
     - Cannot match any of your last 4 passwords

To ensure a smooth onboarding, please share the following with the HR team:
  1. Your active personal email ID (for backup recovery)
  2. Primary contact number and emergency contact
  3. KYC documents (PAN, Aadhaar, address proof, recent photograph)
  4. Educational and prior employment certificates
  5. Bank details for payroll setup

On your first day:
  - Log in to the portal and review your profile under "Staff"
  - Mark your attendance via the "Attendance" module
  - Read all open notices in the Notices section

For any assistance, please reach out to the HR or IT Admin team directly.

Once again, welcome aboard! We look forward to a productive journey together.

Warm regards,
HR Team
${hospitalName}
`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      toast.success('Welcome email copied to clipboard');
    } catch { toast.error('Copy failed'); }
  };

  const openInMail = () => {
    const mailto = `mailto:${u.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const generateTempPassword = async () => {
    if (!staff.id) { toast.error('Staff ID missing'); return; }
    setGenerating(true);
    try {
      const baseURL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
      const token = localStorage.getItem('token');
      // FIX (May 17, 2026): Always prefer the staff record's own hospital_id over
      // localStorage. Super Admin viewing a staff card from Platform Health (or any
      // cross-hospital context) may have an empty selectedHospitalId, which made
      // the backend reject with 400 "No hospital assigned".
      const hospitalId = staff.hospital_id || hospital?.id || localStorage.getItem('selectedHospitalId') || localStorage.getItem('selected_hospital_id');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      if (hospitalId) headers['x-hospital-id'] = hospitalId;
      const res = await window.fetch(`${baseURL}/api/staff/${staff.id}/reset-access`, { method: 'POST', headers });
      let data = null;
      try { data = await res.json(); } catch { /* not json */ }
      if (!res.ok) {
        const msg = (data && (data.detail || data.message)) || `HTTP ${res.status}`;
        toast.error(`Failed to generate temp password: ${msg}`);
        return;
      }
      if (!data || !data.temporary_password) {
        toast.error('Server responded but no temporary_password was returned.');
        return;
      }
      setTempPassword(data.temporary_password);
      toast.success('Temp password ready. Copy the email and share it with the employee.');
    } catch (e) {
      toast.error('Failed to generate: ' + (e.message || 'network error'));
    }
    setGenerating(false);
  };

  const sendViaResend = async () => {
    if (!staff.id) { toast.error('Staff ID missing'); return; }
    if (!u.email) { toast.error('Staff has no email on file'); return; }
    setSending(true);
    try {
      const baseURL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
      const token = localStorage.getItem('token');
      // Same fix as generateTempPassword — Super Admin context may have empty localStorage.
      const hospitalId = staff.hospital_id || hospital?.id || localStorage.getItem('selectedHospitalId');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      if (hospitalId) headers['x-hospital-id'] = hospitalId;
      const res = await window.fetch(`${baseURL}/api/staff/${staff.id}/welcome-email/resend`, { method: 'POST', headers });
      let data = null;
      try { data = await res.json(); } catch { /* not json */ }
      if (!res.ok) {
        const msg = (data && (data.detail || data.message)) || `HTTP ${res.status}`;
        toast.error(`Email send failed: ${msg}`);
        return;
      }
      setTempPassword('Welcome@123');     // backend always uses the fixed temp pw
      setSentInfo({ id: data.email_id, email: data.email });
      toast.success(`Welcome email sent to ${data.email}`);
    } catch (e) {
      toast.error('Failed to send: ' + (e.message || 'network error'));
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Welcome Email — Preview &amp; Send</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <p className="text-xs text-slate-500 mb-1">TO:</p>
            <p className="font-mono text-slate-800 break-all">{u.email}</p>
            <p className="text-xs text-slate-500 mt-2 mb-1">SUBJECT:</p>
            <p className="font-medium text-slate-800">{subject}</p>
          </div>
          {sentInfo && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="welcome-email-sent-banner">
              ✓ Sent via Resend to <span className="font-mono">{sentInfo.email}</span>{sentInfo.id ? ` · email-id ${sentInfo.id.slice(0, 8)}…` : ''}. The login ID + temp password are inside the email.
            </div>
          )}
          {!tempPassword && !sentInfo && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center justify-between gap-3">
              <span><strong>No temp password embedded yet.</strong> Click "Send Welcome Email" to mint a fresh one + email the staff — or "Generate Temp Password" to embed it manually into the preview below.</span>
              <Button size="sm" onClick={generateTempPassword} disabled={generating} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="generate-temp-pw-btn">
                {generating ? 'Generating...' : 'Generate Temp Password'}
              </Button>
            </div>
          )}
          {tempPassword && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800" data-testid="temp-pw-banner">
              <strong>Temp password ready.</strong> Login ID: <span className="font-mono">{loginId}</span> · Temp: <span className="font-mono select-all">{tempPassword}</span>. Both are embedded in the email body below.
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-lg p-4 max-h-80 overflow-y-auto">
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{body}</pre>
          </div>
          <p className="text-xs text-slate-500"><Mail className="inline h-3 w-3 mr-1" /> Staff logs in with Employee ID (User ID) + the Temporary Password above, then sets their own.</p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button variant="outline" onClick={copyToClipboard} data-testid="copy-welcome-email-btn">
              <Copy className="h-4 w-4 mr-1.5" /> Copy
            </Button>
            <Button variant="outline" onClick={openInMail} data-testid="open-welcome-email-btn">
              <Send className="h-4 w-4 mr-1.5" /> Open in Mail
            </Button>
            <Button onClick={sendViaResend} disabled={sending || !u.email} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-welcome-email-btn">
              <Mail className="h-4 w-4 mr-1.5" /> {sending ? 'Sending…' : (sentInfo ? 'Resend Email' : 'Send Welcome Email')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
